import { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";

const PAIRS = [
  {
    slug: "rim-refurb",
    title: "Rim Refurb",
    tag: "Wheel Services",
    note: "Heavy kerb rash sanded out, machined face refinished to a factory shine.",
  },
  {
    slug: "rim-respray",
    title: "Rim Respray",
    tag: "Wheel Services",
    note: "Rust and flaking paint stripped back — refinished in chrome or silver.",
  },
  {
    slug: "steering-rewrap",
    title: "SW Soft Peel",
    tag: "Steering Wheel",
    note: "Disintegrating soft-touch coating stripped and reborn in fresh textured trim.",
  },
  {
    slug: "suede-clean",
    title: "Suede Clean",
    tag: "Steering Wheel",
    note: "Grime build up on suede grips deep cleaned and revived to factory texture.",
  },
  {
    slug: "seat-repair",
    title: "Seat Repair",
    tag: "Interior & Trim",
    note: "Worn-through leather bolster repaired, welded and re-dyed to a factory match.",
  },
  {
    slug: "vinyl-weld",
    title: "Vinyl Weld",
    tag: "Interior & Trim",
    note: "Torn leather panel welded, textured and colour-matched — gone without a trace.",
  },
  {
    slug: "headlight-restore",
    title: "Headlight Resto",
    tag: "Lighting",
    note: "Hazed, yellowed lens machined and UV-sealed back to crystal clarity.",
  },
];

const CompareSlider = ({ pair, index }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(4, Math.min(96, pct)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    update(e.clientX ?? e.touches?.[0]?.clientX);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    update(e.clientX ?? e.touches?.[0]?.clientX);
  };
  const onUp = () => {
    dragging.current = false;
  };

  return (
    <motion.figure
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      className="group"
    >
      <div
        ref={ref}
        data-testid={`gallery-slider-${pair.slug}`}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onTouchStart={onDown}
        onTouchMove={onMove}
        onTouchEnd={onUp}
        className="relative aspect-[4/3] rounded-xl overflow-hidden border border-edge group-hover:border-volt/50 cursor-ew-resize select-none transition-colors duration-300"
      >
        <img
          src={`/gallery/${pair.slug}-after.jpg`}
          alt={`${pair.title} after restoration`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
        <img
          src={`/gallery/${pair.slug}-before.jpg`}
          alt={`${pair.title} before restoration`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          draggable={false}
        />

        <span className="absolute top-3 left-3 font-mono2 text-[9px] uppercase tracking-[0.25em] bg-carbon/70 backdrop-blur px-2.5 py-1 rounded-full text-slate-300 border border-edge">
          Before
        </span>
        <span className="absolute top-3 right-3 font-mono2 text-[9px] uppercase tracking-[0.25em] bg-carbon/70 backdrop-blur px-2.5 py-1 rounded-full text-volt border border-volt/40">
          After
        </span>

        <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
          <div className="absolute inset-y-0 -left-px w-0.5 bg-volt shadow-[0_0_12px_rgba(0,240,255,0.8)]" />
          <div
            data-testid={`gallery-handle-${pair.slug}`}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-carbon border-2 border-volt flex items-center justify-center text-volt shadow-[0_0_16px_rgba(0,240,255,0.5)]"
          >
            <MoveHorizontal size={16} />
          </div>
        </div>
      </div>
      <figcaption className="mt-4 flex items-start justify-between gap-4">
        <div>
          <div className="font-syne font-bold uppercase tracking-wide text-slate-100">{pair.title}</div>
          <p className="text-sm text-slate-400 mt-1">{pair.note}</p>
        </div>
        <span className="shrink-0 font-mono2 text-[9px] uppercase tracking-[0.25em] text-slate-500 border border-edge rounded-full px-3 py-1.5 mt-1">
          {pair.tag}
        </span>
      </figcaption>
    </motion.figure>
  );
};

const GallerySection = () => (
  <section id="gallery" data-testid="gallery-section" className="relative py-20 sm:py-28 lg:py-36">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-volt mb-4">
          04 / The Proof
        </div>
        <h2 className="font-syne font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-slate-100 leading-tight max-w-3xl">
          Before <span className="text-outline-volt">&amp;</span> <span className="metallic-text">after.</span>
        </h2>
        <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-xl">
          Real jobs, straight from the van. Drag the handle on each frame to see the transformation.
        </p>
      </motion.div>

      <div className="mt-14 grid md:grid-cols-2 gap-8 lg:gap-10">
        {PAIRS.map((pair, i) => (
          <CompareSlider key={pair.slug} pair={pair} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default GallerySection;
